import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, message, Spin } from 'antd';
import { axiosInstant } from 'src/api/api';
import ScreeningCriteriaForm from './ScreeningCriteriaForm';

interface ScreeningCriteriaModalProps {
  visible: boolean;
  jobId: string | null;
  onClose: () => void;
  onSave?: (criteria: any) => void;
}

const ScreeningCriteriaModal: React.FC<ScreeningCriteriaModalProps> = ({
  visible,
  jobId,
  onClose,
  onSave
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && jobId) {
      fetchScreeningCriteria();
    }
  }, [visible, jobId]);

  const fetchScreeningCriteria = async () => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      const response = await axiosInstant.get(`/jobs/${jobId}/screening-criteria`);
      if (response.data.success) {
        const criteria = response.data.screeningCriteria;
        setInitialValues(criteria);
        form.setFieldsValue(criteria);
      }
    } catch (error) {
      console.error('Error fetching screening criteria:', error);
      message.error('Failed to load screening criteria');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!jobId) return;
    
    try {
      const values = await form.validateFields();
      
      // Validate weights sum to 100
      const totalWeight = values.experienceWeight + values.skillsWeight + 
                         values.educationWeight + values.locationWeight;
      
      if (totalWeight !== 100) {
        message.error(`Weights must sum to 100%. Current total: ${totalWeight}%`);
        return;
      }

      setSaving(true);
      const response = await axiosInstant.patch(`/jobs/${jobId}/screening-criteria`, values);
      
      if (response.data.success) {
        message.success('Screening criteria updated successfully!');
        if (onSave) {
          onSave(values);
        }
        onClose();
      } else {
        message.error(response.data.message || 'Failed to update screening criteria');
      }
    } catch (error: any) {
      console.error('Error saving screening criteria:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to save screening criteria');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setInitialValues(null);
    onClose();
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
          🎯 Configure Screening Criteria
        </span>
      }
      visible={visible}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel} size="large">
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          size="large"
        >
          Save Criteria
        </Button>
      ]}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading screening criteria...</p>
        </div>
      ) : (
        <Form form={form} layout="vertical">
          <ScreeningCriteriaForm
            form={form}
            initialValues={initialValues}
          />
        </Form>
      )}
    </Modal>
  );
};

export default ScreeningCriteriaModal;

